import React, { useEffect, useState } from 'react';
import _ from 'lodash';

import { ContextualMenuItemType } from '@fluentui/react';
import { IconButton } from '@fluentui/react/lib/Button';

// Interfaces > Utils > Hooks > ClassNames
import { Property } from '../../../api-client';
import { SortingComparer } from '../../../utils/data-comparer';
import { DateTimeEnchanter } from '../../../utils/data-enchanters';

import { useProperties } from './components/PropertiesFormDrawer/hooks/useProperties';
import { useClassNames } from './properties.classNames';

import { IAIApplicationLayout } from '../../../common/layouts/IAIApplicationLayout';
import { IAICalloutColumn } from '../../../common/components/IAIDataTable/components/IAIColumns/IAICalloutColumn';
import { IAIDateColumn } from '../../../common/components/IAIDataTable/components/IAIColumns/IAIDateColumn';
import { IAIDataTable } from '../../../common/components/IAIDataTable';
import { IAIDialog } from '../../../common/components/IAIDialog';
import { PropertiesFormDrawer } from './components/PropertiesFormDrawer';

const PropertiesPage: React.FunctionComponent = () => {
    const {
        handleProperties,
        properties,
        propertiesLoading,
        handleCreateProperties,
        createPropertiesLoading,
        handleRetrieveProperties,
        retrieveProperties,
        retrievePropertiesLoading,
        handleUpdateProperties,
        updatePropertiesLoading,
        handleDeleteProperties,
        deletePropertiesLoading
    } = useProperties();

    const {
        pageContainer,
        calloutSecondaryHeading,
        calloutText
    } = useClassNames();

    useEffect(() => {
        handleProperties();
    }, []);

    const _handleSearchProperties = (entry: Property, searchValue: string): boolean => (
        entry.displayName?.toLowerCase().search(searchValue) !== -1
    );

    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

    const _handleSubmit = (properties: Property, announcementId?: string) => {
        !announcementId
            ?
            handleCreateProperties({
                properties: properties,
                onSuccess: () => {
                    setDrawerVisible(false);
                    handleProperties();
                }
            })
            :
            handleUpdateProperties({
                id: announcementId,
                properties: properties,
                onSuccess: () => {
                    setDrawerVisible(false);
                    handleProperties();
                }
            });
    }

    const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);

    const [delegatedProperties, setDelegatedProperties] = useState<Property>();

    return (
        <IAIApplicationLayout
            icon={'AllApps'}
            heading={'Proprietà'}
            secondaryHeading={'Gestisci le Proprietà Personalizzate.'}
        >
            <div className={pageContainer}>
                <IAIDataTable
                    columns={[
                        {
                            key: 'property_preview__',
                            name: '',
                            minWidth: 50,
                            maxWidth: 50,
                            onRender: (element: any) => (
                                <IAICalloutColumn
                                    title={element.displayName}
                                    tooltipContent={'Anteprima proprietà'}
                                    contentLoading={retrievePropertiesLoading}
                                    content={
                                        retrieveProperties &&
                                        <div className="col-12">
                                            <span className={calloutSecondaryHeading}>
                                                {`Ultimo aggiornamento: ${DateTimeEnchanter.formatStandardDate(retrieveProperties.createdAt, 'it-IT')}`}
                                            </span>
                                            <span className={calloutText}>
                                                {/* {retrieveProperties.displayName} */}
                                                <hr />
                                                <div>
                                                    {_.map(retrieveProperties.values, (value, dataIndex) => (
                                                        <div key={dataIndex}>{value}</div>
                                                    ))}
                                                </div>
                                            </span>
                                        </div>
                                    }
                                    onEngage={() => handleRetrieveProperties({
                                        id: element.id,
                                        onRetrieveSuccess: (properties) => setDelegatedProperties(properties)
                                    })}
                                    onDismiss={() => setDelegatedProperties(undefined)}
                                />
                            )
                        },
                        {
                            key: 'property_displayName__',
                            fieldName: 'displayName',
                            name: 'Nome',
                            minWidth: 50,
                            isResizable: true,
                            sorter: SortingComparer.stringComparer
                        },
                        // {
                        //     key: 'property_values__',
                        //     fieldName: 'values',
                        //     name: 'Valori',
                        //     minWidth: 250,
                        //     isResizable: true,
                        //     onRender: (element: any) => (
                        //         <div>
                        //             {
                        //                 element && element.values.length > 0 ?
                        //                     _.map(element.values, (value, dataIndex) => (
                        //                         <div key={dataIndex}>{value}</div>
                        //                     ))
                        //                 : ''
                        //             }
                        //         </div>
                        //     )
                        // },
                        {
                            key: 'property_action__',
                            fieldName: '',
                            name: '',
                            minWidth: 50,
                            maxWidth: 50,
                            onRender: (element: any, dataIndex?: number) => (
                                <div className="text-center">
                                    <IconButton
                                        menuIconProps={{ iconName: 'CollapseMenu' }}
                                        menuProps={{
                                            items: [
                                                {
                                                    key: `${dataIndex}_menu_header__`,
                                                    itemType: ContextualMenuItemType.Header,
                                                    text: 'Azioni'
                                                },
                                                {
                                                    key: `${dataIndex}_menu_update__`,
                                                    iconProps: { iconName: 'EditCreate' },
                                                    text: 'Modifica',
                                                    onClick: () => {
                                                        handleRetrieveProperties({
                                                            id: element.id,
                                                            onRetrieveSuccess: (properties) => {
                                                                setDelegatedProperties(properties);
                                                                setDrawerVisible(true);
                                                            }
                                                        });
                                                    }
                                                },
                                                {
                                                    key: `${dataIndex}_menu_delete__`,
                                                    iconProps: { iconName: 'Delete' },
                                                    text: 'Elimina',
                                                    onClick: () => {
                                                        setDelegatedProperties(element);
                                                        setDeleteDialogVisible(true);
                                                    }
                                                }
                                            ]
                                        }}
                                    />
                                </div>
                            )
                        }
                    ]}
                    dataSource={properties || []}
                    dataLoading={propertiesLoading}
                    dataTableCommands={[{
                        disabled: propertiesLoading,
                        text: 'Nuova Proprietà',
                        iconProps: { iconName: 'Add' },
                        onClick: () => {
                            setDelegatedProperties(undefined)
                            setDrawerVisible(true)
                        }
                    }]}
                    emptyContentLabel="Nessuna richiesta disponibile"
                    searchable
                    onSearch={_handleSearchProperties}
                />

                <IAIDialog
                    dialogProps={{
                        hidden: !deleteDialogVisible,
                        onDismissed: () => setDelegatedProperties(undefined)
                    }}
                    title={'Eliminazione Proprietà'}
                    subText={`Stai per eliminare la proprietà "${delegatedProperties?.displayName}". Vuoi continuare?`}
                    disabled={deletePropertiesLoading || retrievePropertiesLoading}
                    confirmText={'Elimina'}
                    dismissText={'Annulla'}
                    onConfirm={() => {
                        if (delegatedProperties && delegatedProperties.id) {
                            handleDeleteProperties({
                                id: delegatedProperties.id,
                                onSuccess: () => {
                                    setDeleteDialogVisible(false);
                                    handleProperties();
                                }
                            });
                        }
                    }}
                    onDismiss={() => setDeleteDialogVisible(false)}
                />

                <PropertiesFormDrawer
                    disabled={retrievePropertiesLoading || createPropertiesLoading || updatePropertiesLoading}
                    drawerVisible={drawerVisible}
                    properties={delegatedProperties}
                    onDrawerDismiss={() => setDrawerVisible(false)}
                    onSubmit={_handleSubmit}
                />
            </div>
        </IAIApplicationLayout>
    );
}

export default PropertiesPage;